// src/features/plan/DirectiveDrawer.jsx
//
// PLAN-STRATEGY-BAND-S1: Right-side drawer for creating/editing/viewing a
// strategic directive.  Uses the shared SlideSheet shell.
//
// Props:
//   directive   {object|null} — null = closed; {} = new; directive = edit mode
//   canEdit     {boolean}     — hides write controls when false
//   markets     {Array}       — all available markets for the multi-select
//   onClose     {function}    — close the drawer
//   onSaved     {function}    — called after a successful save/delete

import React, { useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import { Modal } from "../../components/Modal.jsx";
import {
  createDirective,
  updateDirective,
  deleteDirective,
} from "./directivesClient.js";
import {
  DIRECTIVE_TYPE_LABELS,
  DIRECTIVE_TIMEFRAME_LABELS,
  DIRECTIVE_DRIVER_LABELS,
  DIRECTIVE_STATUS_LABELS,
} from "./StrategyBand.jsx";
import { log } from "../../utils/logger.js";

// ─── Field helpers ────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title:            "",
  type:             "hub_designation",
  timeframe:        "now",
  driver:           "strategy",
  status:           "in_review",
  owner:            "",
  note:             "",
  capabilityNeeds:  "",
  marketIds:        [],
};

function directiveToForm(d) {
  if (!d || !d.id) return { ...EMPTY_FORM };
  return {
    title:           d.title ?? "",
    type:            d.type ?? "hub_designation",
    timeframe:       d.timeframe ?? "now",
    driver:          d.driver ?? "strategy",
    status:          d.status ?? "in_review",
    owner:           d.owner ?? "",
    note:            d.note ?? "",
    capabilityNeeds: (d.capabilityNeeds ?? []).join(", "),
    marketIds:       (d.markets ?? []).map((m) => m.marketId),
  };
}

// ─── Shared field row layout ──────────────────────────────────────────────────

function FieldRow({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize:    T.fsXxs,
        fontWeight:  T.fwBold,
        color:       T.mid,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        marginBottom: 5,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width:       "100%",
  border:      `1px solid ${T.border}`,
  borderRadius: 7,
  padding:     "8px 10px",
  fontSize:    T.fsSm,
  color:       T.dark,
  background:  T.white,
  fontFamily:  "inherit",
  boxSizing:   "border-box",
};

const selectStyle = { ...inputStyle, appearance: "none" };

// ─── DrawerBody ───────────────────────────────────────────────────────────────

function DrawerBody({ directive, canEdit, markets, onClose, onSaved }) {
  const isNew = !directive?.id;
  const [form,    setForm]    = useState(() => directiveToForm(directive));
  const [saving,  setSaving]  = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error,   setError]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setForm(directiveToForm(directive));
    setError(null);
    setConfirmDelete(false);
  }, [directive]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleMarket(marketId) {
    setForm((f) => {
      const ids = f.marketIds.includes(marketId)
        ? f.marketIds.filter((id) => id !== marketId)
        : [...f.marketIds, marketId];
      return { ...f, marketIds: ids };
    });
  }

  async function handleSave() {
    if (!form.title.trim()) { setError("Title is required."); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title:            form.title.trim(),
        type:             form.type,
        timeframe:        form.timeframe,
        driver:           form.driver,
        status:           form.status,
        owner:            form.owner.trim() || null,
        note:             form.note.trim() || null,
        capability_needs: form.capabilityNeeds
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        markets:          form.marketIds.map((id) => ({ marketId: id })),
      };
      const result = isNew
        ? await createDirective(payload)
        : await updateDirective(directive.id, payload);
      onSaved(result);
    } catch (err) {
      log.warn("DirectiveDrawer: save failed", { err: String(err) });
      setError(err?.body?.error ?? err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    setError(null);
    try {
      await deleteDirective(directive.id);
      onSaved(null);
    } catch (err) {
      log.warn("DirectiveDrawer: delete failed", { err: String(err) });
      setError(err?.body?.error ?? err?.message ?? "Delete failed");
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  return (
    <div style={{ padding: "20px 20px 28px", overflowY: "auto", maxHeight: "calc(90vh - 60px)" }}>
      <FieldRow label="Title">
        <input
          data-testid="directive-title"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder="e.g. Establish trading hubs"
          disabled={!canEdit}
          style={inputStyle}
        />
      </FieldRow>

      <FieldRow label="Type">
        <select
          data-testid="directive-type"
          value={form.type}
          onChange={(e) => set("type", e.target.value)}
          disabled={!canEdit}
          style={selectStyle}
        >
          {Object.entries(DIRECTIVE_TYPE_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </FieldRow>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <FieldRow label="Timeframe">
          <select
            data-testid="directive-timeframe"
            value={form.timeframe}
            onChange={(e) => set("timeframe", e.target.value)}
            disabled={!canEdit}
            style={selectStyle}
          >
            {Object.entries(DIRECTIVE_TIMEFRAME_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </FieldRow>

        <FieldRow label="Driver">
          <select
            data-testid="directive-driver"
            value={form.driver}
            onChange={(e) => set("driver", e.target.value)}
            disabled={!canEdit}
            style={selectStyle}
          >
            {Object.entries(DIRECTIVE_DRIVER_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </FieldRow>
      </div>

      <FieldRow label="Status">
        <select
          data-testid="directive-status"
          value={form.status}
          onChange={(e) => set("status", e.target.value)}
          disabled={!canEdit}
          style={selectStyle}
        >
          {Object.entries(DIRECTIVE_STATUS_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </FieldRow>

      <FieldRow label="Owner">
        <input
          data-testid="directive-owner"
          value={form.owner}
          onChange={(e) => set("owner", e.target.value)}
          placeholder="Name or team"
          disabled={!canEdit}
          style={inputStyle}
        />
      </FieldRow>

      <FieldRow label="Capability needs (comma-separated)">
        <input
          data-testid="directive-capability-needs"
          value={form.capabilityNeeds}
          onChange={(e) => set("capabilityNeeds", e.target.value)}
          placeholder="e.g. engineering, product"
          disabled={!canEdit}
          style={inputStyle}
        />
      </FieldRow>

      {markets && markets.length > 0 && (
        <FieldRow label={`Markets (${form.marketIds.length} selected)`}>
          <div
            data-testid="directive-markets-multiselect"
            style={{
              border:     `1px solid ${T.border}`,
              borderRadius: 7,
              maxHeight:  160,
              overflowY:  "auto",
            }}
          >
            {markets.map((m) => {
              const checked = form.marketIds.includes(m.id);
              return (
                <label
                  key={m.id}
                  style={{
                    display:    "flex",
                    alignItems: "center",
                    gap:        8,
                    padding:    "7px 10px",
                    fontSize:   T.fsSm,
                    cursor:     canEdit ? "pointer" : "default",
                    background: checked ? T.blueSoft : "transparent",
                    borderBottom: `1px solid ${T.border}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => canEdit && toggleMarket(m.id)}
                    disabled={!canEdit}
                    style={{ flexShrink: 0 }}
                  />
                  <span style={{ flex: 1, color: T.dark }}>{m.market ?? m.name ?? m.id}</span>
                  <span style={{ fontSize: T.fsXxs, color: T.light }}>{m.region}</span>
                </label>
              );
            })}
          </div>
        </FieldRow>
      )}

      <FieldRow label="Note">
        <textarea
          data-testid="directive-note"
          value={form.note}
          onChange={(e) => set("note", e.target.value)}
          placeholder="Optional context or background…"
          disabled={!canEdit}
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </FieldRow>

      {error && (
        <div style={{
          background:   T.ragRedBg,
          color:        T.ragRed,
          borderRadius: 7,
          padding:      "8px 12px",
          fontSize:     T.fsXs,
          marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      {canEdit && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            type="button"
            data-testid="directive-save-btn"
            disabled={saving}
            onClick={handleSave}
            style={{
              flex:        1,
              background:  T.blue,
              color:       T.white,
              border:      "none",
              borderRadius: 8,
              padding:     "10px 0",
              fontSize:    T.fsSm,
              fontWeight:  T.fwBold,
              cursor:      saving ? "wait" : "pointer",
              fontFamily:  "inherit",
            }}
          >
            {saving ? "Saving…" : isNew ? "Create directive" : "Save changes"}
          </button>

          <button
            type="button"
            data-testid="directive-cancel-btn"
            onClick={onClose}
            style={{
              background:  "none",
              border:      `1px solid ${T.border}`,
              borderRadius: 8,
              padding:     "10px 16px",
              fontSize:    T.fsSm,
              fontWeight:  T.fwSemibold,
              color:       T.mid,
              cursor:      "pointer",
              fontFamily:  "inherit",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {canEdit && !isNew && (
        <div style={{ marginTop: 24, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
          {confirmDelete ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: T.fsXs, color: T.ragRed, flex: 1 }}>
                Delete this directive permanently?
              </span>
              <button
                type="button"
                data-testid="directive-confirm-delete-btn"
                disabled={deleting}
                onClick={handleDelete}
                style={{
                  background:   T.ragRed,
                  color:        T.white,
                  border:       "none",
                  borderRadius: 7,
                  padding:      "7px 14px",
                  fontSize:     T.fsXs,
                  fontWeight:   T.fwBold,
                  cursor:       deleting ? "wait" : "pointer",
                  fontFamily:   "inherit",
                }}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                style={{
                  background:   "none",
                  border:       `1px solid ${T.border}`,
                  borderRadius: 7,
                  padding:      "7px 12px",
                  fontSize:     T.fsXs,
                  fontWeight:   T.fwSemibold,
                  color:        T.mid,
                  cursor:       "pointer",
                  fontFamily:   "inherit",
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="directive-delete-btn"
              onClick={handleDelete}
              style={{
                background:  "none",
                border:      "none",
                padding:     0,
                fontSize:    T.fsXs,
                fontWeight:  T.fwSemibold,
                color:       T.ragRed,
                cursor:      "pointer",
                fontFamily:  "inherit",
              }}
            >
              Delete directive
            </button>
          )}
        </div>
      )}

      {!canEdit && (
        <div style={{ marginTop: 8, padding: "8px 12px", background: T.lightGray, borderRadius: 7, fontSize: T.fsXs, color: T.mid }}>
          You have view-only access. Contact an editor or admin to make changes.
        </div>
      )}
    </div>
  );
}

// ─── Exported DirectiveDrawer ─────────────────────────────────────────────────

/**
 * @param {object}         props
 * @param {object|null}    props.directive   — null=closed, {}=new, obj=edit
 * @param {boolean}        props.canEdit     — show write controls when true
 * @param {Array}          props.markets     — all available markets
 * @param {function}       props.onClose     — close handler
 * @param {function}       props.onSaved     — called after save/delete
 */
export function DirectiveDrawer({ directive, canEdit, markets = [], onClose, onSaved }) {
  const isOpen = directive !== null;
  const isNew = !directive?.id;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isNew ? "New directive" : "Edit directive"}
      ariaLabelledBy="directive-modal-title"
      testId="directive-modal"
      overlayTestId="directive-modal-overlay"
      maxWidth={560}
    >
      {isOpen && (
        <DrawerBody
          directive={directive}
          canEdit={canEdit}
          markets={markets}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </Modal>
  );
}
